import { CheckCircle2, CircleHelp, Clock3, Info, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const MILDEW_STATES = {
  NO_RISK: {
    label: "Sin riesgo",
    description: "La última evaluación no detecta condiciones de riesgo en este punto.",
    icon: CheckCircle2,
    className: "text-stable",
    surfaceClassName: "border-stable/30 bg-stable/5",
  },
  RISK: {
    label: "Riesgo detectado",
    description: "Este punto de monitorización contribuye al riesgo de mildiu de su parcela.",
    icon: TriangleAlert,
    className: "text-critical",
    surfaceClassName: "border-critical/30 bg-critical/5",
  },
  UNKNOWN: {
    label: "Datos insuficientes",
    description: "No se puede confirmar un estado de mildiu con los datos actuales.",
    icon: CircleHelp,
    className: "text-moderate",
    surfaceClassName: "border-moderate/30 bg-moderate/5",
  },
};

const EVALUATION_LABELS = {
  CURRENT: "Evaluación actual",
  STALE: "Evaluación obsoleta",
  NEVER_EVALUATED: "Evaluación pendiente",
};

const REASON_LABELS = {
  qualifying_episode: "La lluvia, la humedad y la temperatura recientes cumplen las condiciones de riesgo.",
  rain_gate_not_met: "No se ha alcanzado la lluvia reciente necesaria para indicar riesgo.",
  moisture_proxy_not_met: "La humedad medida no alcanza el umbral de riesgo.",
  temperature_outside_v1_band: "La temperatura medida está fuera del intervalo de riesgo.",
  duration_incomplete: "Las condiciones aún no han durado lo suficiente para indicar riesgo.",
  incomplete_packet_pair: "Faltan mediciones de hoja o de aire para completar la evaluación.",
  telemetry_gap: "Hay un intervalo demasiado largo entre mediciones para completar la evaluación.",
  out_of_order_observation: "Se recibió una medición fuera del orden esperado.",
  invalid_observation: "Una medición recibida no es válida para la evaluación.",
  configuration_missing: "Falta configurar la frecuencia de seguimiento de este dispositivo.",
  telemetry_stale: "Las mediciones necesarias ya no son lo bastante recientes.",
  weather_unavailable: "No hay datos meteorológicos actuales para completar la evaluación.",
  monitoring_disabled: "El seguimiento de mildiu no está activado para esta parcela.",
  weather_quality_provisional: "Los datos meteorológicos son provisionales; la evaluación sigue siendo orientativa.",
  risk_hold_active: "El riesgo se mantiene durante el periodo de seguridad posterior a un episodio detectado.",
  evaluation_not_run: "El evaluador de mildiu aún no se ha ejecutado para este dispositivo.",
};

const formatTime = (value) => value
  ? new Intl.DateTimeFormat("es-ES", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
  : "Sin datos";

export const getMildewPresentation = (mildew) => {
  if (!mildew) {
    return {
      ...MILDEW_STATES.UNKNOWN,
      label: "Sin evaluación disponible",
      description: "No hay una evaluación de mildiu disponible para la asignación actual.",
      evaluationLabel: "Sin evaluación disponible",
    };
  }

  const state = MILDEW_STATES[mildew.state] || MILDEW_STATES.UNKNOWN;
  return {
    ...state,
    evaluationLabel: EVALUATION_LABELS[mildew.evaluation_status] || "Estado de evaluación desconocido",
  };
};

export const getMildewReasonLabel = (reason) => REASON_LABELS[reason] || `Motivo informado por el evaluador: ${reason}`;

export function MildewStatus({ mildew, compact = false, showDetailsButton = false, className, testId }) {
  const presentation = getMildewPresentation(mildew);
  const Icon = presentation.icon;
  const reasons = mildew?.reason_codes || [];

  return (
    <section className={cn("rounded-lg border px-4 py-3", presentation.surfaceClassName, className)} data-testid={testId} aria-label="Estado de mildiu">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <Icon className={cn("mt-0.5 shrink-0", presentation.className)} size={18} aria-hidden="true" />
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-tertiary">Mildiu</div>
            <div className={cn("font-semibold", presentation.className)}>{presentation.label}</div>
            <p className="mt-1 text-xs leading-5 text-ink-secondary">{presentation.description}</p>
            <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-ink-secondary"><Clock3 size={13} aria-hidden="true" /> {presentation.evaluationLabel}</div>
          </div>
        </div>
        {compact && showDetailsButton && (
          <Dialog>
            <DialogTrigger asChild>
              <button type="button" className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-line bg-surface px-3 py-2 text-xs font-semibold text-ink-secondary hover:bg-page hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand">
                <Info size={14} aria-hidden="true" />
                Detalles del mildiu
              </button>
            </DialogTrigger>
            <DialogContent className="border-line bg-surface text-ink sm:max-w-2xl">
              <DialogHeader className="pr-8">
                <DialogTitle className="font-heading text-ink">Detalles del mildiu</DialogTitle>
                <DialogDescription className="text-ink-secondary">{presentation.description}</DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-md border border-line bg-page p-4 text-sm sm:grid-cols-2">
                <div>
                  <div className="text-xs text-ink-secondary">Estado</div>
                  <div className={cn("mt-1 font-semibold", presentation.className)}>{presentation.label}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-secondary">Evaluación</div>
                  <div className="mt-1 font-semibold">{presentation.evaluationLabel}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-secondary">Inicio del estado</div>
                  <div className="mt-1 font-semibold">{formatTime(mildew?.state_since)}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-secondary">Última evaluación</div>
                  <div className="mt-1 font-semibold">{formatTime(mildew?.last_evaluated_at)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-ink-secondary">Datos de entrada</div>
                  <div className={cn("mt-1 font-semibold", mildew?.inputs_current === true ? "text-stable" : "text-moderate")}>
                    {mildew?.inputs_current === true ? "Actuales" : mildew ? "Pendientes o antiguos" : "No disponibles"}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-ink">Motivo de la evaluación</h3>
                <div className="mt-2 grid gap-2 text-sm leading-5 text-ink-secondary sm:grid-cols-2">
                  {reasons.length
                    ? reasons.map((reason) => <p key={reason}>{getMildewReasonLabel(reason)}</p>)
                    : <p>No hay un motivo adicional disponible.</p>}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!compact && (
        <dl className="mt-3 grid gap-2 border-t border-line/70 pt-3 text-sm">
          <div className="flex items-start justify-between gap-4"><dt className="text-ink-secondary">Inicio del estado</dt><dd className="text-right font-semibold">{formatTime(mildew?.state_since)}</dd></div>
          <div className="flex items-start justify-between gap-4"><dt className="text-ink-secondary">Última evaluación</dt><dd className="text-right font-semibold">{formatTime(mildew?.last_evaluated_at)}</dd></div>
          <div className="flex items-start justify-between gap-4"><dt className="text-ink-secondary">Datos de entrada</dt><dd className={cn("text-right font-semibold", mildew?.inputs_current === true ? "text-stable" : "text-moderate")}>{mildew?.inputs_current === true ? "Actuales" : mildew ? "Pendientes o antiguos" : "No disponibles"}</dd></div>
          <div className="border-t border-line/70 pt-3"><dt className="text-ink-secondary">Motivo de la evaluación</dt><dd className="mt-1.5 space-y-1 text-xs leading-5 text-ink">{reasons.length ? reasons.map((reason) => <div key={reason}>{getMildewReasonLabel(reason)}</div>) : <div>No hay un motivo adicional disponible.</div>}</dd></div>
        </dl>
      )}
    </section>
  );
}
